import { useRoute, useRouter } from 'nuxt/app'
import { ref, watch } from 'vue'
import type {
    LocationQueryRaw,
    LocationQueryValue,
    LocationQueryValueRaw,
} from 'vue-router'
import type { ZodObject, ZodRawShape, ZodTypeAny } from 'zod'
import { ZodDefault, ZodNullable, ZodOptional, z } from 'zod'

type Primitive = string | number | boolean | null | undefined
type L5QueryValue = Primitive | Primitive[]
type L5QueryRecord = Record<string, L5QueryValue>

function unwrapZodType(type: ZodTypeAny): ZodTypeAny {
    while (
        type instanceof ZodOptional ||
        type instanceof ZodNullable ||
        type instanceof ZodDefault
    ) {
        type = type._def.innerType
    }
    return type
}

function castQueryValue(
    type: ZodTypeAny,
    value: LocationQueryValue | LocationQueryValue[]
): L5QueryValue {
    const baseType = unwrapZodType(type)
    if (value === null || value === undefined || value === '') return null

    switch (baseType._def.typeName) {
        case 'ZodNumber':
            return Number.isNaN(Number(value)) ? null : Number(value)
        case 'ZodBoolean':
            return value.toString() === 'true' || value.toString() === '1'
        case 'ZodString':
            return String(value)
        case 'ZodArray':
            return Array.isArray(value) ? value : [value]
        default:
            return value
    }
}

function deepEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b)
}

function toLocationQueryValue(
    value: L5QueryValue
): LocationQueryValueRaw | LocationQueryValueRaw[] {
    if (Array.isArray(value)) {
        return value
            .filter((item) => item !== undefined)
            .map((item) =>
                typeof item === 'boolean' ? String(item) : (item ?? null)
            )
    }

    if (typeof value === 'boolean') {
        return String(value)
    }

    return value
}

function toLocationQueryRaw(query: L5QueryRecord): LocationQueryRaw {
    return Object.fromEntries(
        Object.entries(query).map(([key, value]) => [
            key,
            toLocationQueryValue(value),
        ])
    )
}

interface UseL5Options<T> {
    defaults?: Partial<BaseL5Keys> &
        Partial<Record<keyof T & string, L5QueryValue>>
    fromQuery?: boolean
    ignoreKeys?: (keyof T & string)[]
    include?: string[]
    queryMap?: Partial<Record<keyof T & string, string>>
}

export type BaseL5Keys = {
    page: number
    limit: number
    sortedBy: string
    orderBy: string
    searchJoin: 'or' | 'and'
    searchFields: string
    search: string
}

const BASE_EXCLUDE_KEYS: (keyof BaseL5Keys)[] = [
    'searchFields',
    'orderBy',
    'sortedBy',
    'searchJoin',
    'page',
    'limit',
    'search',
]

export function useL5<T extends ZodObject<ZodRawShape>>(
    schema: T,
    options: UseL5Options<z.infer<T>> = {}
) {
    const route = useRoute()
    const router = useRouter()

    const {
        defaults,
        fromQuery = false,
        ignoreKeys = [],
        include = [],
        queryMap = {},
    } = options

    const reverseQueryMap: Record<string, string> = Object.fromEntries(
        Object.entries(queryMap).map(([key, val]) => [val, key])
    )

    const shape = schema.shape as Record<string, ZodTypeAny>
    const allKeys = Object.keys(shape)
    const defaultsRecord = (defaults ?? {}) as Partial<L5QueryRecord> &
        Partial<BaseL5Keys>
    const queryMapRecord = queryMap as Record<string, string>

    const systemDefault: BaseL5Keys = {
        page: 1,
        limit: 10,
        sortedBy: 'desc',
        orderBy: 'id',
        searchJoin: 'and',
        searchFields: '',
        search: '',
    }

    const getSystemValue = <K extends keyof BaseL5Keys>(
        key: K,
        parser: ZodTypeAny,
        queryKey: LocationQueryValue | LocationQueryValue[]
    ): BaseL5Keys[K] => {
        const fromQueryValue = castQueryValue(parser, queryKey)
        if (fromQueryValue !== null && fromQueryValue !== undefined) {
            return fromQueryValue as BaseL5Keys[K]
        }
        if (defaultsRecord[key] !== undefined) {
            return defaultsRecord[key] as BaseL5Keys[K]
        }
        return systemDefault[key]
    }

    const initialDefaults: L5QueryRecord = {}
    for (const key of allKeys) {
        const def = shape[key]
        if (defaultsRecord[key] !== undefined) {
            initialDefaults[key] = defaultsRecord[key]
        } else {
            const type = unwrapZodType(def)
            switch (type._def.typeName) {
                case 'ZodString':
                case 'ZodNumber':
                    initialDefaults[key] = null
                    break
                case 'ZodBoolean':
                    initialDefaults[key] = false
                    break
                case 'ZodArray':
                    initialDefaults[key] = []
                    break
                default:
                    initialDefaults[key] = null
            }
        }
    }

    const baseFilters: L5QueryRecord = {}
    for (const key of allKeys) {
        const def = shape[key]
        const value = castQueryValue(def, route.query[key])
        baseFilters[key] = value ?? defaultsRecord[key] ?? initialDefaults[key]
    }

    type L5State = z.infer<T> & BaseL5Keys

    const l5Filters = ref<L5State>({
        ...baseFilters,
        page: getSystemValue('page', z.number(), route.query.page),
        limit: getSystemValue('limit', z.number(), route.query.limit),
        sortedBy: getSystemValue('sortedBy', z.string(), route.query.sortedBy),
        orderBy: getSystemValue('orderBy', z.string(), route.query.orderBy),
        searchJoin: getSystemValue(
            'searchJoin',
            z.string(),
            route.query.searchJoin
        ),
        searchFields: getSystemValue(
            'searchFields',
            z.string(),
            route.query.searchFields
        ),
        search: getSystemValue('search', z.string(), route.query.search),
    })

    const effectiveIgnoreKeys = [
        ...new Set([...BASE_EXCLUDE_KEYS, ...ignoreKeys.map(String)]),
    ]

    const l5ToQuery = (query: L5QueryRecord) => {
        const result: L5QueryRecord = {
            page: l5Filters.value.page,
            limit: l5Filters.value.limit,
            sortedBy: l5Filters.value.sortedBy,
            orderBy: l5Filters.value.orderBy,
            searchJoin: l5Filters.value.searchJoin,
            searchFields: l5Filters.value.searchFields,
        }

        for (let key of ignoreKeys) {
            const val = query[key as string]
            if (val !== undefined && val !== null && val !== '') {
                if (key in queryMapRecord) {
                    key = queryMapRecord[key]
                }
                result[key as string] = val
            }
        }
        if (query.search && query.search !== '') {
            result.search = query.search
        } else {
            const search = Object.entries(query)
                .filter(
                    ([key, value]) =>
                        !effectiveIgnoreKeys.includes(key) &&
                        key !== 'search' &&
                        value !== null &&
                        value !== undefined &&
                        value !== ''
                )
                .map(([key, value]) => {
                    let _key = key
                    if (_key in queryMapRecord) {
                        _key = queryMapRecord[_key]
                    }
                    return `${_key}:${value}`
                })
                .join(';')
            result.search = search
        }

        if (include.length > 0) {
            result.include = include.join(',')
        }

        return result
    }

    const l5Query = ref<L5QueryRecord>(
        l5ToQuery(l5Filters.value as L5QueryRecord)
    )

    const l5UpdateQuery = (newFilters: Partial<Record<string, any>>) => {
        for (const key of Object.keys(newFilters)) {
            if (Object.prototype.hasOwnProperty.call(shape, key)) {
                const parsed = castQueryValue(shape[key], newFilters[key])
                l5Filters.value[key] = parsed ?? null
            } else {
                l5Filters.value[key] = newFilters[key]
            }
        }

        const filters = l5Filters.value as L5QueryRecord
        const query: L5QueryRecord = {}

        for (const [key, value] of Object.entries(filters)) {
            if (value === null || value === undefined || value === '') continue

            const defaultValue =
                defaultsRecord[key] ?? systemDefault[key as keyof BaseL5Keys]
            if (
                fromQuery &&
                defaultValue !== undefined &&
                deepEqual(value, defaultValue)
            )
                continue

            query[key] = value
        }

        if (fromQuery) {
            router.push({ query: toLocationQueryRaw(query) })
        } else {
            l5Query.value = l5ToQuery(l5Filters.value as L5QueryRecord)
        }
    }

    const l5ResetFilters = () => {
        for (const key of allKeys) {
            const raw = route.query[key]
            const def = shape[key]
            const parsed = castQueryValue(def, raw)
            const fallback = defaultsRecord[key] ?? initialDefaults[key]
            l5Filters.value[key] = parsed ?? fallback
        }

        l5Filters.value.page = getSystemValue(
            'page',
            z.number(),
            route.query.page
        )
        l5Filters.value.limit = getSystemValue(
            'limit',
            z.number(),
            route.query.limit
        )
        l5Filters.value.sortedBy = getSystemValue(
            'sortedBy',
            z.string(),
            route.query.sortedBy
        )
        l5Filters.value.orderBy = getSystemValue(
            'orderBy',
            z.string(),
            route.query.orderBy
        )
        l5Filters.value.searchJoin = getSystemValue(
            'searchJoin',
            z.string(),
            route.query.searchJoin
        )
        l5Filters.value.searchFields = getSystemValue(
            'searchFields',
            z.string(),
            route.query.searchFields
        )
        l5Filters.value.search = getSystemValue(
            'search',
            z.string(),
            route.query.search
        )

        l5Query.value = l5ToQuery(l5Filters.value as L5QueryRecord)
    }

    watch(
        () => route.fullPath,
        () => {
            if (!fromQuery) return

            for (const [queryKey, value] of Object.entries(route.query)) {
                const schemaKey = reverseQueryMap[queryKey] ?? queryKey
                if (!shape[schemaKey]) continue

                const def = shape[schemaKey]
                const fallback =
                    defaultsRecord[schemaKey] ?? initialDefaults[schemaKey]
                const parsed = castQueryValue(def, value)
                l5Filters.value[schemaKey] = parsed ?? fallback
            }

            l5Filters.value.page = getSystemValue(
                'page',
                z.number(),
                route.query.page
            )
            l5Filters.value.limit = getSystemValue(
                'limit',
                z.number(),
                route.query.limit
            )
            l5Filters.value.sortedBy = getSystemValue(
                'sortedBy',
                z.string(),
                route.query.sortedBy
            )
            l5Filters.value.orderBy = getSystemValue(
                'orderBy',
                z.string(),
                route.query.orderBy
            )
            l5Filters.value.searchJoin = getSystemValue(
                'searchJoin',
                z.string(),
                route.query.searchJoin
            )
            l5Filters.value.searchFields = getSystemValue(
                'searchFields',
                z.string(),
                route.query.searchFields
            )
            l5Filters.value.search = getSystemValue(
                'search',
                z.string(),
                route.query.search
            )

            l5Query.value = l5ToQuery(l5Filters.value as L5QueryRecord)
        }
    )

    return {
        l5Filters,
        l5UpdateQuery,
        l5ResetFilters,
        l5Query,
        l5ToQuery,
    }
}
